import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { hasChecklistTemplate } from '@/lib/checklists/registry';
import { calcularCobertura, type CoberturaResult, type TipoProxima } from '@/lib/coberturaRevisiones';

export interface OtProgramada {
  id: string;
  numero_ot: string;
  estado: string;
  fecha_limite: string | null;
  hora_ejecucion: string | null;
  tecnicoNombre: string | null;
}

export interface SistemaRevisionRow {
  sistemaId: string;
  tipo: string;
  ubicacion: string | null;
  clienteId: string;
  clienteNombre: string;
  sedeId: string | null;
  sedeNombre: string | null;
  sedeDireccion: string | null;
  sedePoblacion: string | null;
  omitido: boolean;
  anclaPeriodo: string | null;
  anclaTipo: TipoProxima | null;
  cobertura: CoberturaResult;
  ot?: OtProgramada;
}

interface RawChecklist {
  fecha: string | null;
  created_at: string;
  periodo: string | null;
  tipo_revision: string;
  estado: string;
  numero_trimestral: number | null;
  sistema_id: string | null;
  archivado: boolean | null;
}

const PAGE = 1000;
const ESTADOS_OT_ABIERTAS = ['creada', 'asignada', 'en_progreso'];

async function fetchAll(table: string, columns: string, apply?: (q: any) => any): Promise<any[]> {
  const rows: any[] = [];
  for (let from = 0; ; from += PAGE) {
    let q: any = (supabase as any).from(table).select(columns);
    if (apply) q = apply(q);
    const { data, error } = await q.range(from, from + PAGE - 1);
    if (error) throw error;
    rows.push(...(data ?? []));
    if (!data || data.length < PAGE) break;
  }
  return rows;
}

export function useMantenimientosDashboard() {
  const { perfil } = useAuth();
  const [rows, setRows] = useState<SistemaRevisionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    if (!perfil?.empresa_id) return;
    setLoading(true);
    setError(null);
    try {
      const [sistemas, clientes, sedes, checklists, vinculos] = await Promise.all([
        fetchAll('sistemas', 'id, tipo, ubicacion, cliente_id, sede_id, activo, cobertura_omitida, revision_ancla_periodo, revision_ancla_tipo'),
        fetchAll('clientes', 'id, nombre, activo'),
        fetchAll('cliente_sedes', 'id, nombre, direccion, poblacion'),
        fetchAll(
          'checklists_seguridad',
          'fecha, created_at, periodo, tipo_revision, estado, numero_trimestral, sistema_id, archivado, modo',
          (q: any) => q.neq('tipo_revision', 'rapida').neq('modo', 'elementos'),
        ),
        fetchAll(
          'orden_trabajo_sistemas',
          `sistema_id, orden:ordenes_trabajo!inner(
             id, numero_ot, estado, fecha_limite, hora_ejecucion,
             tecnico_asignado:perfiles!ordenes_trabajo_tecnico_asignado_id_fkey(nombre)
           )`,
          (q: any) => q.in('orden.estado', ESTADOS_OT_ABIERTAS),
        ).catch(() => []),
      ]);

      const clientesMap = new Map<string, any>(clientes.map((c: any) => [c.id, c]));
      const sedesMap = new Map<string, any>(sedes.map((s: any) => [s.id, s]));

      const otsPorSistema = new Map<string, OtProgramada>();
      (vinculos as any[]).forEach(v => {
        const o = v.orden;
        if (!o || !v.sistema_id) return;
        if (!otsPorSistema.has(v.sistema_id)) {
          otsPorSistema.set(v.sistema_id, {
            id: o.id,
            numero_ot: o.numero_ot,
            estado: o.estado,
            fecha_limite: o.fecha_limite ?? null,
            hora_ejecucion: o.hora_ejecucion ?? null,
            tecnicoNombre: o.tecnico_asignado?.nombre ?? null,
          });
        }
      });

      const porSistema: Record<string, RawChecklist[]> = {};
      (checklists as RawChecklist[]).forEach(c => {
        if (!c.sistema_id || c.archivado) return;
        (porSistema[c.sistema_id] ||= []).push(c);
      });

      const result: SistemaRevisionRow[] = sistemas
        .filter((s: any) => s.activo !== false)
        .filter((s: any) => hasChecklistTemplate(s.tipo))
        .filter((s: any) => {
          const cliente = clientesMap.get(s.cliente_id);
          return cliente && cliente.activo !== false;
        })
        .map((s: any) => {
          const cliente = clientesMap.get(s.cliente_id);
          const sede = s.sede_id ? sedesMap.get(s.sede_id) : null;
          return {
            sistemaId: s.id,
            tipo: s.tipo,
            ubicacion: s.ubicacion ?? null,
            clienteId: s.cliente_id,
            clienteNombre: cliente?.nombre ?? 'Cliente',
            sedeId: s.sede_id ?? null,
            sedeNombre: sede?.nombre ?? null,
            sedeDireccion: sede?.direccion ?? null,
            sedePoblacion: sede?.poblacion ?? null,
            omitido: !!s.cobertura_omitida,
            anclaPeriodo: s.revision_ancla_periodo ?? null,
            anclaTipo: (s.revision_ancla_tipo ?? null) as TipoProxima | null,
            ot: otsPorSistema.get(s.id),
            cobertura: calcularCobertura(
              s.tipo,
              (porSistema[s.id] || []).map(c => ({
                fecha: c.fecha || c.created_at,
                periodo: c.periodo,
                tipo_revision: c.tipo_revision,
                estado: c.estado,
                numero_trimestral: c.numero_trimestral,
              })),
              !!s.cobertura_omitida,
              new Date(),
              s.revision_ancla_periodo
                ? { periodo: s.revision_ancla_periodo, tipo: (s.revision_ancla_tipo ?? 'anual') as TipoProxima }
                : null,
            ),
          };
        });

      setRows(result);
    } catch (e: any) {
      console.error('Error cargando mantenimientos', e);
      setError(e?.message ?? 'Error cargando datos');
    } finally {
      setLoading(false);
    }
  }, [perfil?.empresa_id]);

  useEffect(() => { cargar(); }, [cargar]);

  const omitirCobertura = useCallback(async (sistemaId: string, valor: boolean) => {
    const anterior = rows;
    setRows(prev => prev.map(r => (
      r.sistemaId === sistemaId ? { ...r, omitido: valor } : r
    )));
    const { error: err } = await (supabase as any)
      .from('sistemas')
      .update({ cobertura_omitida: valor })
      .eq('id', sistemaId);
    if (err) {
      console.error(err);
      setRows(anterior);
      throw err;
    }
    await cargar();
  }, [rows, cargar]);

  const activos = useMemo(() => rows.filter(r => !r.omitido), [rows]);

  const resumen = useMemo(() => {
    const base = {
      al_dia: 0, proximo: 0, vencido: 0, sin_iniciar: 0,
      requiere_aclaracion: 0, omitido: 0, programadas: 0,
    };
    rows.forEach(r => {
      if (r.omitido) { base.omitido += 1; return; }
      const e = r.cobertura.estado as keyof typeof base;
      if (e in base) base[e] += 1;
      if (r.ot) base.programadas += 1;
    });
    return base;
  }, [rows]);

  const tiposDisponibles = useMemo(
    () => Array.from(new Set(rows.map(r => r.tipo))).sort(),
    [rows],
  );

  return { rows, activos, resumen, tiposDisponibles, loading, error, refetch: cargar, omitirCobertura };
}
